import { isObject } from "lodash";
import type { z } from "zod/v4";

export function notNull<T>(value: T | null): value is T {
  return value !== null;
}

export function notUndefined<T>(value: T | undefined): value is T {
  return value !== undefined;
}

export function notNullish<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

export function isNullish<T>(value: T | null | undefined): value is null | undefined {
  return value === null || value === undefined;
}

export function numberOrNull<T>(value: T | string | undefined | boolean): value is T {
  return typeof value !== "string" && typeof value !== "boolean" && value !== undefined;
}

export function isPromise(x: unknown | null | undefined): x is Promise<unknown> {
  return notNullish(x) && isObject(x) && "then" in x;
}

export function assertUnreachable(_x: never): never {
  throw new Error(`Didn't expect to get here, got: ${_x}`);
}

export function isZodSchema<T extends z.ZodType>(maybeSchema: T | unknown): maybeSchema is T {
  return (
    isObject(maybeSchema) &&
    "parse" in maybeSchema &&
    typeof maybeSchema.parse === "function" &&
    "def" in maybeSchema &&
    isObject(maybeSchema.def) &&
    "meta" in maybeSchema &&
    typeof maybeSchema.meta === "function"
  );
}

export function assertNever(_x: never) {
  //intentionally not throwing for usage, where we want typescript
  //to help us, but still provide a default in case something
  //bad happens in production
}

export function assertEmptyObject(_x: Record<string, never>) {
  //intentionally not throwing for usage, where we want typescript
  //to help us, but still provide a default in case something
  //bad happens in production
}

export type KeyNotNullish<T, K extends keyof T> = {
  [P in keyof T]: P extends K ? Exclude<T[P], null | undefined> : T[P];
};

export const fieldNotNullish =
  <K extends string>(requiredProp: K) =>
  <T extends Partial<Record<K, unknown>>>(value: T): value is KeyNotNullish<T, K> & Required<Pick<T, K>> =>
    notNullish(value[requiredProp]);
